import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Archive, Loader2, Eye, EyeOff } from "lucide-react";

export default function AuthPage() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("echo_hunter");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    let result;
    if (mode === "login") {
      result = await login(email, password);
    } else {
      if (!name.trim()) {
        setError("Name is required");
        setLoading(false);
        return;
      }
      result = await register(email, password, name, role);
    }

    if (!result.success) {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#F9F7F1] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-2">
            <Archive size={28} className="text-[#1A2B3C]" />
            <span className="font-serif text-2xl font-medium text-[#1A1A1A] tracking-tight">
              Echoes from the Past
            </span>
          </div>
          <p className="text-sm text-[#718096]">Historical storytelling platform</p>
        </div>

        {/* Card */}
        <div className="bg-white border border-[#E5E2D9] p-8" data-testid="auth-form-card">
          <h2 className="font-serif text-xl font-medium text-[#1A1A1A] mb-6" data-testid="auth-title">
            {mode === "login" ? "Sign In" : "Create Account"}
          </h2>

          {error && (
            <div className="mb-4 p-3 border border-red-200 bg-red-50 text-sm text-red-700" data-testid="auth-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "register" && (
              <>
                <div>
                  <label className="font-mono text-xs uppercase tracking-widest text-[#718096] block mb-1.5">
                    Name
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-3 py-2.5 border border-[#E5E2D9] bg-white text-sm focus:ring-2 focus:ring-[#1A2B3C] focus:border-transparent outline-none"
                    placeholder="Your display name"
                    data-testid="register-name-input"
                    required
                  />
                </div>
                <div>
                  <label className="font-mono text-xs uppercase tracking-widest text-[#718096] block mb-1.5">
                    I am an...
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      className={`p-3 border text-center text-sm transition-all ${role === "echo_maker" ? "border-[#B85C38] bg-[#B85C38]/5 text-[#B85C38]" : "border-[#E5E2D9] text-[#4A5568] hover:border-[#1A2B3C]"}`}
                      onClick={() => setRole("echo_maker")}
                      data-testid="role-echo-maker"
                    >
                      <strong className="block">Echoe Maker</strong>
                      <span className="text-xs">Create stories</span>
                    </button>
                    <button
                      type="button"
                      className={`p-3 border text-center text-sm transition-all ${role === "echo_hunter" ? "border-[#B85C38] bg-[#B85C38]/5 text-[#B85C38]" : "border-[#E5E2D9] text-[#4A5568] hover:border-[#1A2B3C]"}`}
                      onClick={() => setRole("echo_hunter")}
                      data-testid="role-echo-hunter"
                    >
                      <strong className="block">Echoe Hunter</strong>
                      <span className="text-xs">Discover & favorite</span>
                    </button>
                  </div>
                </div>
              </>
            )}

            <div>
              <label className="font-mono text-xs uppercase tracking-widest text-[#718096] block mb-1.5">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2.5 border border-[#E5E2D9] bg-white text-sm focus:ring-2 focus:ring-[#1A2B3C] focus:border-transparent outline-none"
                placeholder="you@email.com"
                data-testid="auth-email-input"
                required
              />
            </div>

            <div>
              <label className="font-mono text-xs uppercase tracking-widest text-[#718096] block mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2.5 pr-10 border border-[#E5E2D9] bg-white text-sm focus:ring-2 focus:ring-[#1A2B3C] focus:border-transparent outline-none"
                  placeholder={mode === "register" ? "Min 6 characters" : "Your password"}
                  data-testid="auth-password-input"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#718096] hover:text-[#1A2B3C] transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                  data-testid="toggle-password-visibility"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="btn-primary w-full justify-center"
              disabled={loading}
              data-testid="auth-submit-btn"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : (mode === "login" ? "Sign In" : "Create Account")}
            </button>
          </form>

          <div className="mt-6 pt-4 border-t border-[#E5E2D9] text-center">
            {mode === "login" ? (
              <p className="text-sm text-[#4A5568]">
                Don't have an account?{" "}
                <button
                  className="text-[#B85C38] font-medium hover:underline"
                  onClick={() => { setMode("register"); setError(""); }}
                  data-testid="switch-to-register"
                >
                  Sign Up
                </button>
              </p>
            ) : (
              <p className="text-sm text-[#4A5568]">
                Already have an account?{" "}
                <button
                  className="text-[#B85C38] font-medium hover:underline"
                  onClick={() => { setMode("login"); setError(""); }}
                  data-testid="switch-to-login"
                >
                  Sign In
                </button>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
